import { mediaUrl } from '../utils/media';

export interface EventMedia {
  id: number;
  src: string;
  title: string;
  type: 'image' | 'video';
}

export interface EventAlbum {
  id: string;
  title: string;
  school: string;
  date: string;
  chip: string;
  description: string;
  coverSrc: string;
  items: EventMedia[];
}

export interface EventGallery {
  albums: EventAlbum[];
  photoCount: number;
}

const eventImagesBase = '/events/images';
const imageExtensions = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);
const siteImageNames = new Set([
  'about-1.jpg',
  'about-2.jpg',
  'about-header.jpg',
  'carousel-1.jpg',
  'carousel-2.jpg',
  'causes-header.jpg',
  'contact-header.jpg',
  'courses-1.jpg',
  'courses-2.jpg',
  'courses-3.jpg',
  'events-header.jpg',
  'icon-1.png',
  'icon-3.png',
  'team-ananad-mashatte.jpg',
  'team-header.jpg',
  'team-mahesh-biradar.jpg',
  'team-prashant-swamy.jpg',
  'testimonial-1.jpg',
  'testimonial-2.jpg',
  'testimonial-3.jpg',
  'testimonial-header.jpg',
]);

const VISITS: Array<{
  id: string;
  school: string;
  date: string;
  chip: string;
  description: string;
  videos?: EventMedia[];
}> = [
  {
    id: 'jun19',
    school: 'School visit',
    date: '19–20 June 2026',
    chip: '19–20 June',
    description: 'Pen and notebook distribution during the first June school visits in Bidar.',
  },
  {
    id: 'lata-mangeshkar',
    school: 'Lata Mangeshkar Girls School',
    date: '22 June 2026',
    chip: 'Lata Mangeshkar',
    description: 'Notebook distribution at Lata Mangeshkar Girls School, Kamalnagar.',
  },
  {
    id: 'jun27',
    school: 'School visit',
    date: '27 June 2026',
    chip: '27 June',
    description: 'Classroom and campus moments from the 27 June school visit.',
  },
  {
    id: 'chandeshwar',
    school: 'Chandeshwar School',
    date: '29 June 2026',
    chip: 'Chandeshwar',
    description: 'Distribution day at Chandeshwar Government School.',
    videos: [
      {
        id: 10002,
        src: mediaUrl('chandeshwar-school.mp4'),
        title: 'Chandeshwar School visit video',
        type: 'video',
      },
    ],
  },
  {
    id: 'hakyal',
    school: 'Hakyal School',
    date: 'School outreach',
    chip: 'Hakyal',
    description: 'Outreach and notebook distribution at Hakyal Government School.',
    videos: [
      {
        id: 10003,
        src: mediaUrl('hakyal-school.mp4'),
        title: 'Hakyal School visit video',
        type: 'video',
      },
    ],
  },
  {
    id: 'jul06',
    school: 'School visits',
    date: '6–8 July 2026',
    chip: '6–8 July',
    description: 'Later July outreach across Bidar schools during the pen and notebooks drive.',
  },
  {
    id: 'other',
    school: 'Other photographs',
    date: '2026 Drive',
    chip: 'Other',
    description: 'Additional photos from the 2026 pen and notebooks drive.',
    videos: [
      {
        id: 10001,
        src: mediaUrl('pen-notebooks-highlight.mp4'),
        title: 'Pen & Notebooks Drive highlights',
        type: 'video',
      },
    ],
  },
];

function visitIdFromFileName(fileName: string): string {
  const whatsapp = fileName.match(/IMG-(\d{8})/i);
  if (whatsapp) {
    const raw = whatsapp[1];
    const date = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
    if (date === '2026-06-19' || date === '2026-06-20') {
      return 'jun19';
    }
    if (date === '2026-06-22') {
      return 'lata-mangeshkar';
    }
    if (date === '2026-06-27') {
      return 'jun27';
    }
    if (date === '2026-06-29') {
      return 'chandeshwar';
    }
    if (date === '2026-07-06' || date === '2026-07-08') {
      return 'jul06';
    }
  }

  if (/DSC\d+/i.test(fileName)) {
    return 'chandeshwar';
  }

  return 'other';
}

function albumTitle(school: string, date: string): string {
  return `${school} — ${date}`;
}

function isImageFile(name: string): boolean {
  const extension = name.split('.').pop()?.toLowerCase() || '';
  return imageExtensions.has(extension);
}

interface ListedEventImage {
  name: string;
  filePath?: string;
}

function imageSrc(file: ListedEventImage): string {
  if (file.filePath) {
    return file.filePath.startsWith('/') ? file.filePath : `/${file.filePath}`;
  }
  return `${eventImagesBase}/${file.name}`;
}

export function buildEventAlbums(files: ListedEventImage[]): EventGallery {
  const photosByVisit: Record<string, EventMedia[]> = {};

  files
    .filter((file) => isImageFile(file.name) && !siteImageNames.has(file.name))
    .sort((left, right) => left.name.localeCompare(right.name))
    .forEach((file, index) => {
      const visitId = visitIdFromFileName(file.name);
      const visit = VISITS.find((entry) => entry.id === visitId) || VISITS[VISITS.length - 1];
      const media: EventMedia = {
        id: index + 1,
        src: imageSrc(file),
        title: albumTitle(visit.school, visit.date),
        type: 'image',
      };
      photosByVisit[visitId] = photosByVisit[visitId] || [];
      photosByVisit[visitId].push(media);
    });

  const albums = VISITS.map((visit) => {
    const photos = photosByVisit[visit.id] || [];
    const items = [...(visit.videos || []), ...photos];
    const coverSrc = photos[0]?.src || mediaUrl('events-header.jpg');

    return {
      id: visit.id,
      title: albumTitle(visit.school, visit.date),
      school: visit.school,
      date: visit.date,
      chip: visit.chip,
      description: visit.description,
      coverSrc,
      items,
    };
  }).filter((album) => album.items.length > 0);

  return {
    albums,
    photoCount: files.filter((file) => isImageFile(file.name) && !siteImageNames.has(file.name)).length,
  };
}

export async function loadEventAlbums(): Promise<EventGallery> {
  const response = await fetch('/event-images.json');
  const payload = await response.json().catch(() => null);
  if (!response.ok || !Array.isArray(payload?.files)) {
    throw new Error(payload?.message || 'Could not load event photos');
  }

  const files: ListedEventImage[] = payload.files
    .map((file: { name?: string; filePath?: string }) => ({
      name: file?.name || '',
      filePath: file?.filePath || '',
    }))
    .filter((file: ListedEventImage) => file.name);
  return buildEventAlbums(files);
}

export const eventVideoCount = VISITS.reduce((count, visit) => count + (visit.videos?.length || 0), 0);
export const defaultEventAlbumId = 'chandeshwar';
